import React, { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useHistory } from 'react-router-dom';
import { AutoSizer, List, CellMeasurer, CellMeasurerCache } from 'react-virtualized';
import { Box, Grid } from '@material-ui/core';
import { Menu as MenuIcon } from '@material-ui/icons';
import { useToggle, useWindowSize, useMeasure } from 'react-use';
import { AppBar, ScreenLoader, Page, EmptyState, SearchBox } from 'components';
import { ru } from 'locale';
import { authStore, anketsStore, userStore } from 'stores';
import { NoData as NoDataBg } from 'resources/images';
import AnketaItem from './components/AnketaItem';
import Sidebar from './components/Sidebar';
import Filters from './components/Filters';
import options from './AnketsScreen.mock';
import useStyles from './AnketsScreen.styles';

const cache = new CellMeasurerCache({
  fixedWidth: true,
  defaultHeight: 164,
});

const AnketsScreen = observer(() => {
  const classes = useStyles();
  const history = useHistory();
  const [searchQuery, setSearchQuery] = useState('');
  const [isVisibleFilter, toggleFilter] = useToggle(false);
  const { ankets, fetchState, activeFilter, setFilter } = anketsStore;
  const { userData, fetchState: fetchUserState } = userStore;
  const [isSideMenuOpen, toggleSideMenu] = useToggle(false);
  const { height: pageHeight } = useWindowSize();
  const [appBarRef, { height: appBarHeight }] = useMeasure();
  const [searchBoxRef, { height: searchBoxHeight }] = useMeasure();
  const [filtersRef, { height: filtersHeight }] = useMeasure();
  const listRef = useRef(null);
  const listHeight = isVisibleFilter
    ? pageHeight - appBarHeight - searchBoxHeight - filtersHeight
    : pageHeight - appBarHeight - searchBoxHeight;
  const isLoading = fetchUserState === 'pending';
  const hasAnkets = ankets.length !== 0;

  useEffect(() => {
    if (!hasAnkets) {
      anketsStore.getAnkets();
    }
  }, [hasAnkets]);

  const handleItemSelect = id => {
    history.push(`/ankets/${id}`);
  };

  const handleSignOut = () => {
    authStore.signOut();
  };

  const handleClear = () => {
    setSearchQuery('');
  };

  const handleChange = ({ target }) => {
    setSearchQuery(target.value);
  };

  const handleSearch = () => {
    handleItemSelect(searchQuery);
    handleClear();
  };

  const handleFilterChange = ({ target }) => {
    setFilter(target.value);

    if (listRef.current) {
      cache.clearAll();
    }
  };

  const rowRenderer = rowData => {
    const { index, parent, key, style } = rowData;
    const item = ankets[index];

    return (
      <CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
        <div style={style}>
          <Box mb={1}>
            <AnketaItem item={item} onClick={handleItemSelect} />
          </Box>
        </div>
      </CellMeasurer>
    );
  };

  const renderAnkets = () => {
    if (!hasAnkets) {
      return (
        <EmptyState
          image={{ source: NoDataBg, alt: ru.listOfAnkets }}
          title={ru.emptyList(ru.listOfAnkets)}
        />
      );
    }

    const rowCount = ankets.length;

    return (
      <AutoSizer disableHeight>
        {({ width }) => (
          <List
            ref={listRef}
            height={listHeight}
            overscanRowCount={10}
            rowCount={rowCount}
            deferredMeasurementCache={cache}
            rowHeight={cache.rowHeight}
            rowRenderer={rowRenderer}
            width={width}
          />
        )}
      </AutoSizer>
    );
  };

  return (
    <Page fullScreen style={{ minHeight: '100rvh' }} className={classes.root}>
      <Grid container direction="column" wrap="nowrap">
        <Grid item>
          <AppBar
            ref={appBarRef}
            title={ru.ankets}
            navIcon={MenuIcon}
            hideOnScroll
            navButtonAriaLabel={ru.openSideMenu}
            onNavButtonClick={toggleSideMenu}
          />
        </Grid>

        {fetchState === 'pending' && <ScreenLoader />}

        <Box mb={1}>
          <SearchBox
            ref={searchBoxRef}
            value={searchQuery}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
            onChange={handleChange}
            onSearch={handleSearch}
            onClear={handleClear}
            onFilterSelect={toggleFilter}
            showFilters
          />
          {isVisibleFilter && (
            <Filters
              ref={filtersRef}
              activeFilter={activeFilter}
              options={options}
              onChange={handleFilterChange}
            />
          )}
        </Box>

        <Grid
          container
          item
          direction="column"
          justify={hasAnkets ? 'flex-start' : 'center'}
          className={classes.listOfAnkets}
        >
          {fetchState === 'fulfilled' && renderAnkets()}
        </Grid>
      </Grid>

      <Sidebar
        open={isSideMenuOpen}
        account={userData}
        onClose={toggleSideMenu}
        onSignOut={handleSignOut}
        loading={isLoading}
      />
    </Page>
  );
});

export default AnketsScreen;
